<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>類別管理</h1>
    <button class="btn-primary" @click="openAddModal">+ 新增類別</button>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋類別名稱..."
        @input="handleSearch"
      />
    </div>

    <div class="filter-dropdown">
      <select v-model="activeFilter" class="form-input">
        <option
          v-for="filter in dynamicFilters"
          :key="filter.value"
          :value="filter.value"
        >
          {{ filter.label }} ({{ filter.count ?? 0 }})
        </option>
      </select>
    </div>
  </div>

  <!-- 手機版 - 類別卡片列表 -->
  <div class="category-list-mobile">
    <!-- 母類別卡片 -->
    <div
      class="category-card parent-card"
      v-for="parent in pagedCategories"
      :key="parent.id"
    >
      <!-- 母類別標題 -->
      <div class="category-header">
        <div class="category-title-section">
          <div class="category-basic-info">
            <h3 class="category-name">{{ parent.name }}</h3>
            <div class="badge-row">
              <span class="type-badge parent">母類別</span>
              <span class="status-badge" :class="parent.status">
                {{ parent.status === "active" ? "啟用" : "停用" }}
              </span>
            </div>
          </div>
        </div>
        <div class="category-actions-top category-actions-desktop">
          <button
            class="btn-icon-small"
            @click="editCategory(parent)"
            title="編輯"
          >
            <img src="@/assets/image/icon/edit.png" class="all-icon-small" />
          </button>
          <button
            class="btn-icon-small"
            @click="deleteCategory(parent)"
            title="刪除"
          >
            <img src="@/assets/image/icon/trash.png" class="all-icon-small" />
          </button>
        </div>

        <!-- 手機/平板版文字按鈕 -->
        <div class="category-actions-mobile">
          <button
            class="btn-mobile-category-action"
            @click="editCategory(parent)"
          >
            編輯
          </button>

          <button
            class="btn-mobile-category-action delete"
            @click="deleteCategory(parent)"
          >
            刪除
          </button>
        </div>
      </div>

      <!-- 母類別資訊 -->
      <div class="category-info">
        <div class="info-row">
          <span class="info-label">子類別數量</span>
          <span class="info-value">{{ parent.children?.length || 0 }} 個</span>
        </div>
      </div>

      <!-- 子類別列表 -->
      <div
        class="children-list"
        v-if="parent.children && parent.children.length > 0"
      >
        <div class="children-header">
          <span class="children-title">子類別列表</span>
          <button class="btn-add-child" @click="openAddChildModal(parent)">
            + 新增子類別
          </button>
        </div>
        <div
          class="child-item"
          v-for="child in parent.children"
          :key="child.id"
        >
          <div class="child-info">
            <span class="child-name">{{ child.name }}</span>
            <span class="status-badge small" :class="child.status">
              {{ child.status === "active" ? "啟用" : "停用" }}
            </span>
          </div>
          <div class="child-actions child-actions-desktop">
            <button
              class="btn-child-action"
              @click="editCategory(child, parent)"
              title="編輯"
            >
              <img src="@/assets/image/icon/edit.png" class="all-icon-small" />
            </button>
            <button
              class="btn-child-action delete"
              @click="deleteCategory(child, parent)"
              title="刪除"
            >
              <img src="@/assets/image/icon/trash.png" class="all-icon-small" />
            </button>
          </div>

          <!-- 手機/平板版文字按鈕 -->
          <div class="child-actions-mobile">
            <button
              class="btn-mobile-child-action"
              @click="editCategory(child, parent)"
            >
              編輯
            </button>

            <button
              class="btn-mobile-child-action delete"
              @click="deleteCategory(child, parent)"
            >
              刪除
            </button>
          </div>
        </div>
      </div>

      <!-- 無子類別提示 -->
      <div class="no-children" v-else>
        <span class="no-children-text">尚無子類別</span>
        <button class="btn-add-child-inline" @click="openAddChildModal(parent)">
          + 新增子類別
        </button>
      </div>
    </div>
  </div>

  <!-- ✅ 手機版分頁 -->
  <div class="pagination-container mobile-pagination">
    <button
      class="pagination-btn"
      @click="prevPage"
      :disabled="currentPage === 1"
    >
      ◀ 上一頁
    </button>
    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>
    <button
      class="pagination-btn"
      @click="nextPage"
      :disabled="currentPage >= totalPages"
    >
      下一頁 ▶
    </button>
  </div>

  <!-- 電腦版 - 類別表格 -->
  <div class="category-table-desktop">
    <div class="table-container">
      <table class="category-table">
        <thead>
          <tr>
            <th>類別名稱</th>
            <th style="text-align: center">類型</th>
            <th>母類別</th>
            <th style="text-align: center">子類別數</th>
            <th>建立日期</th>
            <th style="text-align: center">狀態</th>
            <th style="text-align: center">操作</th>
          </tr>
        </thead>
        <tbody>
          <!-- ✅ 顯示母類別 + 子類別（原本模式） -->
          <template v-if="activeFilter !== 'child'">
            <template v-for="parent in pagedCategories" :key="parent.id">
              <!-- 母類別行 -->
              <tr class="parent-row">
                <td>
                  <div class="category-cell">
                    <button
                      class="expand-btn"
                      @click="toggleExpand(parent.id)"
                      v-if="parent.children && parent.children.length > 0"
                    >
                      {{ expandedIds.includes(parent.id) ? "▼" : "▶" }}
                    </button>
                    <span class="category-icon">📁</span>
                    <span class="category-name-text">{{ parent.name }}</span>
                  </div>
                </td>
                <td class="type-cell">
                  <span class="type-badge parent">母類別</span>
                </td>
                <td>-</td>
                <td class="count-cell">
                  <span class="count-number">{{
                    parent.children?.length || 0
                  }}</span>
                </td>
                <td>{{ parent.createdAt }}</td>
                <td class="status-cell">
                  <span class="status-badge" :class="parent.status">
                    {{ parent.status === "active" ? "啟用" : "停用" }}
                  </span>
                </td>
                <td class="actions-cell">
                  <div class="table-actions">
                    <button
                      class="btn-table-action-top"
                      @click="openAddChildModal(parent)"
                      title="新增子類別"
                    >
                      <img
                        src="@/assets/image/icon/plus.png"
                        class="all-icon"
                      />
                    </button>
                    <button
                      class="btn-table-action-top"
                      @click="editCategory(parent)"
                      title="編輯"
                    >
                      <img
                        src="@/assets/image/icon/edit.png"
                        class="all-icon"
                      />
                    </button>
                    <button
                      class="btn-table-action-top delete"
                      @click="deleteCategory(parent)"
                      title="刪除"
                    >
                      <img
                        src="@/assets/image/icon/trash.png"
                        class="all-icon"
                      />
                    </button>
                  </div>
                </td>
              </tr>

              <!-- 子類別行 -->
              <tr
                v-for="child in parent.children"
                :key="child.id"
                class="child-row"
                v-show="expandedIds.includes(parent.id)"
              >
                <td>
                  <div class="category-cell child">
                    <span class="child-indent">└─</span>
                    <span class="category-name-text">{{ child.name }}</span>
                  </div>
                </td>
                <td class="type-cell">
                  <span class="type-badge child">子類別</span>
                </td>
                <td>{{ parent.name }}</td>
                <td class="count-cell">-</td>
                <td>{{ child.createdAt }}</td>
                <td class="status-cell">
                  <span class="status-badge" :class="child.status">
                    {{ child.status === "active" ? "啟用" : "停用" }}
                  </span>
                </td>
                <td class="actions-cell">
                  <div class="table-actions">
                    <button
                      class="btn-table-action"
                      @click="editCategory(child, parent)"
                      title="編輯"
                    >
                      <img
                        src="@/assets/image/icon/edit.png"
                        class="all-icon"
                      />
                    </button>
                    <button
                      class="btn-table-action delete"
                      @click="deleteCategory(child, parent)"
                      title="刪除"
                    >
                      <img
                        src="@/assets/image/icon/trash.png"
                        class="all-icon"
                      />
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </template>

          <!-- ✅ 顯示子類別模式 -->
          <template v-else>
            <tr v-for="child in pagedCategories" :key="child.id">
              <td>
                <div class="category-cell">
                  <span class="category-icon">📂</span>
                  <span class="category-name-text">{{ child.name }}</span>
                </div>
              </td>
              <td class="type-cell">
                <span class="type-badge child">子類別</span>
              </td>
              <td>{{ child.parentName }}</td>
              <td class="count-cell">-</td>
              <td>{{ child.createdAt }}</td>
              <td class="status-cell">
                <span class="status-badge" :class="child.status">
                  {{ child.status === "active" ? "啟用" : "停用" }}
                </span>
              </td>
              <td class="actions-cell">
                <div class="table-actions">
                  <button
                    class="btn-table-action"
                    @click="editCategory(child)"
                    title="編輯"
                  >
                    ✏️
                  </button>
                  <button
                    class="btn-table-action delete"
                    @click="deleteCategory(child)"
                    title="刪除"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>

  <!-- ✅ 電腦版分頁 (共用樣式) -->
  <div class="pagination-container computer-pagination">
    <button
      class="pagination-btn"
      @click="prevPage"
      :disabled="currentPage === 1"
    >
      ◀ 上一頁
    </button>
    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>
    <button
      class="pagination-btn"
      @click="nextPage"
      :disabled="currentPage >= totalPages"
    >
      下一頁 ▶
    </button>
  </div>

  <!-- 新增/編輯類別 Modal -->
  <div class="modal" v-if="showModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ getModalTitle() }}</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>

      <form @submit.prevent="saveCategory" class="modal-body">
        <!-- 類型選擇 (僅新增時顯示) -->
        <div class="form-group" v-if="!editingCategory">
          <label class="form-label">類別類型 *</label>
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                v-model="formData.type"
                value="parent"
                @change="handleTypeChange"
              />
              <span class="radio-text">
                <span class="radio-icon">📁</span>
                母類別
              </span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="formData.type"
                value="child"
                @change="handleTypeChange"
              />
              <span class="radio-text">
                <span class="radio-icon">📂</span>
                子類別
              </span>
            </label>
          </div>
        </div>

        <!-- 選擇母類別 (子類別時顯示) -->
        <div
          class="form-group"
          v-if="formData.type === 'child' && !preSelectedParent"
        >
          <label class="form-label">選擇母類別 *</label>
          <select class="form-input" v-model="formData.parentId" required>
            <option value="" disabled selected hidden>請選擇母類別</option>
            <option
              v-for="parent in activeParentCategories"
              :key="parent.id"
              :value="parent.id"
            >
              {{ parent.name }}
            </option>
          </select>
        </div>

        <!-- 顯示預選的母類別 -->
        <div class="form-group" v-if="preSelectedParent">
          <label class="form-label">母類別</label>
          <div class="selected-parent">
            <span class="parent-icon">📁</span>
            <span class="parent-name">{{ preSelectedParent.name }}</span>
          </div>
        </div>

        <!-- 類別名稱 -->
        <div class="form-group">
          <label class="form-label">
            {{ formData.type === "parent" ? "母" : "子" }}類別名稱 *
          </label>
          <input
            type="text"
            class="form-input"
            v-model="formData.name"
            :placeholder="
              '請輸入' + (formData.type === 'parent' ? '母' : '子') + '類別名稱'
            "
            required
          />
        </div>

        <!-- 狀態 -->
        <div class="form-group">
          <label class="form-label">狀態 *</label>
          <select class="form-input" v-model="formData.status" required>
            <option value="active">啟用</option>
            <option value="inactive">停用</option>
          </select>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="closeModal">
            取消
          </button>
          <button type="submit" class="btn-submit">
            {{ editingCategory ? "儲存變更" : "新增類別" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script src="@/assets/js/category.js"></script>
<style scoped src="@/assets/css/category.css"></style>
